import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { CONTRACTS } from '../../config/contracts';
import { formatUSDT, parseUSDT, shortenAddress } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';
import PoolEligibleMembers from './PoolEligibleMembers';

const ZERO = '0x0000000000000000000000000000000000000000';
const POOL_NAMES = ['X1 Pool', 'X2 Pool', 'X3 Pool'];
const MEMBERS_PER_PAGE = 25;

function pctToBps(p) {
  const n = Number(p);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function bpsToPct(bps) {
  return (Number(bps) / 100).toFixed(2);
}

// ==========================================
// Members drawer (paginated)
// ==========================================
function PoolMembersDrawer({ poolId, open, onClose }) {
  const { read } = useContracts();
  const [page, setPage] = useState(0);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async (pg) => {
    if (!read.pools) return;
    setLoading(true);
    try {
      const start = pg * MEMBERS_PER_PAGE;
      const res = await read.pools.getPoolMembersPaginated(poolId, start, MEMBERS_PER_PAGE);
      const ids = res.members.map((n) => n.toString());
      setMembers(ids);
      setTotal(Number(res.total));

      // Fetch user info per member (batch)
      const userInfo = {};
      const batch = await Promise.all(
        ids.map((id) => read.xterra.getUser(id).then((u) => ({ id, u })).catch(() => null))
      );
      batch.filter(Boolean).forEach(({ id, u }) => { userInfo[id] = u; });
      setUsers(userInfo);
    } catch {
      setMembers([]);
      setUsers({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, poolId]);

  if (!open) return null;

  const totalPages = Math.max(1, Math.ceil(total / MEMBERS_PER_PAGE));

  return (
    <div style={{
      marginTop: 14, padding: 14,
      background: 'var(--bg-primary)', border: '1px solid var(--border)',
      borderRadius: 12,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, fontSize: 12, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700,
      }}>
        <span>{total} member{total === 1 ? '' : 's'} in {POOL_NAMES[poolId]}</span>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            cursor: 'pointer', fontSize: 16,
          }}
        >×</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 20 }}><Spinner /></div>
      ) : members.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
          No members in this pool yet.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 380, overflowY: 'auto' }}>
            {members.map((id) => {
              const u = users[id];
              return (
                <div
                  key={id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1fr auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: 8,
                    border: '1px solid var(--border-soft)',
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>
                    ID {id}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                    {u ? shortenAddress(u.wallet) : '—'}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-primary)', textAlign: 'right' }}>
                    {u ? `${formatUSDT(u.totalInvestmentUSDT, 0)} USDT` : ''}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <button className="pg-btn" disabled={page === 0} onClick={() => setPage(0)}>«</button>
              <button className="pg-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                Page {page + 1} / {totalPages}
              </span>
              <button className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
              <button className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==========================================
// Threshold editor
// ==========================================
function ThresholdsEditor({ onSaved }) {
  const { read, write } = useContracts();
  const notify = useNotify();

  const [current, setCurrent] = useState(null);
  const [values, setValues] = useState({
    x1Bps: '', x2Bps: '', x3Bps: '',
    x1SelfMin: '', x2SelfMin: '', x3SelfMin: '',
    legBizMinX1: '',
    legsRequired: '',
  });
  const [busy, setBusy] = useState('');

  const load = async () => {
    if (!read.pools) return;
    try {
      const [bps0, bps1, bps2, min0, min1, min2, legBiz, legs] = await Promise.all([
        read.pools.poolBps(0),
        read.pools.poolBps(1),
        read.pools.poolBps(2),
        read.pools.selfInvestmentMin(0),
        read.pools.selfInvestmentMin(1),
        read.pools.selfInvestmentMin(2),
        read.pools.legBusinessMinX1(),
        read.pools.legsRequired(),
      ]);
      const c = {
        x1Bps: bpsToPct(bps0), x2Bps: bpsToPct(bps1), x3Bps: bpsToPct(bps2),
        x1SelfMin: ethers.utils.formatUnits(min0, 18),
        x2SelfMin: ethers.utils.formatUnits(min1, 18),
        x3SelfMin: ethers.utils.formatUnits(min2, 18),
        legBizMinX1: ethers.utils.formatUnits(legBiz, 18),
        legsRequired: legs.toString(),
      };
      setCurrent(c);
      setValues(c);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function saveBps(idx) {
    if (!write?.pools) return notify.error('Pools not available.');
    const key = ['x1Bps', 'x2Bps', 'x3Bps'][idx];
    const bps = pctToBps(values[key]);
    if (bps > 10000) return notify.error('Max 100%.');
    setBusy(key);
    try {
      const tx = await write.pools.setPoolBps(idx, bps);
      notify.info('Updating turnover %...');
      await tx.wait();
      notify.success(`${POOL_NAMES[idx]} turnover % updated.`);
      load();
      onSaved?.();
    } catch (err) { notify.error(parseError(err)); } finally { setBusy(''); }
  }

  async function saveSelfMin(idx) {
    if (!write?.pools) return notify.error('Pools not available.');
    const key = ['x1SelfMin', 'x2SelfMin', 'x3SelfMin'][idx];
    setBusy(key);
    try {
      const tx = await write.pools.setSelfInvestmentMin(idx, parseUSDT(values[key]));
      notify.info('Updating self-investment min...');
      await tx.wait();
      notify.success(`${POOL_NAMES[idx]} threshold updated.`);
      load();
    } catch (err) { notify.error(parseError(err)); } finally { setBusy(''); }
  }

  async function saveLegBiz() {
    if (!write?.pools) return notify.error('Pools not available.');
    setBusy('legBiz');
    try {
      const tx = await write.pools.setLegBusinessMinX1(parseUSDT(values.legBizMinX1));
      notify.info('Updating leg business min (X1)...');
      await tx.wait();
      notify.success('X1 leg business min updated.');
      load();
    } catch (err) { notify.error(parseError(err)); } finally { setBusy(''); }
  }

  async function saveLegs() {
    if (!write?.pools) return notify.error('Pools not available.');
    const n = Number(values.legsRequired);
    if (!isFinite(n) || n <= 0 || n > 20) return notify.error('Must be 1..20.');
    setBusy('legs');
    try {
      const tx = await write.pools.setLegsRequired(n);
      notify.info('Updating legs required...');
      await tx.wait();
      notify.success('Legs required updated.');
      load();
    } catch (err) { notify.error(parseError(err)); } finally { setBusy(''); }
  }

  if (!current) {
    return <div style={{ padding: 12 }}><Spinner /></div>;
  }

  const changed = (k) => values[k] !== current[k];

  return (
    <div style={{ padding: 18, background: 'var(--bg-tertiary)', border: '1px solid var(--border-soft)', borderRadius: 12 }}>
      {/* Turnover % per pool */}
      <h5 style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 12 }}>
        Turnover Share (% of every real investment)
      </h5>
      {[0, 1, 2].map((idx) => {
        const key = ['x1Bps', 'x2Bps', 'x3Bps'][idx];
        return (
          <div key={key} className="admin-form-row">
            <div className="fields">
              <div>
                <label className="small-label">{POOL_NAMES[idx]} turnover %</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="input"
                    value={values[key]}
                    onChange={(e) => setValues({ ...values, [key]: e.target.value.replace(/[^0-9.]/g, '') })}
                  />
                  <span style={{
                    position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 14, pointerEvents: 'none',
                  }}>%</span>
                </div>
              </div>
            </div>
            <button className="btn btn-primary" disabled={busy === key || !changed(key)} onClick={() => saveBps(idx)}>
              {busy === key ? <Spinner /> : 'Update'}
            </button>
          </div>
        );
      })}

      <div className="divider" />

      {/* Self-investment min per pool */}
      <h5 style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 12 }}>
        Min Active Self-Investment (USDT)
      </h5>
      {[0, 1, 2].map((idx) => {
        const key = ['x1SelfMin', 'x2SelfMin', 'x3SelfMin'][idx];
        return (
          <div key={key} className="admin-form-row">
            <div className="fields">
              <div>
                <label className="small-label">{POOL_NAMES[idx]} min self-invest (USDT)</label>
                <input
                  className="input"
                  value={values[key]}
                  onChange={(e) => setValues({ ...values, [key]: e.target.value.replace(/[^0-9.]/g, '') })}
                />
              </div>
            </div>
            <button className="btn btn-primary" disabled={busy === key || !changed(key)} onClick={() => saveSelfMin(idx)}>
              {busy === key ? <Spinner /> : 'Update'}
            </button>
          </div>
        );
      })}

      <div className="divider" />

      {/* Leg business min (X1) */}
      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">X1 Leg Business Min (USDT per leg)</label>
            <input
              className="input"
              value={values.legBizMinX1}
              onChange={(e) => setValues({ ...values, legBizMinX1: e.target.value.replace(/[^0-9.]/g, '') })}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Only applies to X1 eligibility. X2/X3 require X1/X2 downlines instead of leg USDT.
            </div>
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy === 'legBiz' || !changed('legBizMinX1')} onClick={saveLegBiz}>
          {busy === 'legBiz' ? <Spinner /> : 'Update'}
        </button>
      </div>

      {/* Legs required */}
      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Legs Required (all pools · max 20)</label>
            <input
              className="input"
              value={values.legsRequired}
              onChange={(e) => setValues({ ...values, legsRequired: e.target.value.replace(/\D/g, '') })}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Number of direct-referral legs that must meet the criteria (default 2).
            </div>
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy === 'legs' || !changed('legsRequired')} onClick={saveLegs}>
          {busy === 'legs' ? <Spinner /> : 'Update'}
        </button>
      </div>
    </div>
  );
}

// ==========================================
// Main panel
// ==========================================
export default function PoolsPanel() {
  const { read, write } = useContracts();
  const notify = useNotify();

  const [linkedPools, setLinkedPools] = useState(null);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState(null);
  const [distribute, setDistribute] = useState([{ perUser: '' }, { perUser: '' }, { perUser: '' }]);
  const [openMembers, setOpenMembers] = useState(-1);
  const [poolAdmin, setPoolAdmin] = useState({ userId: '', poolId: '0' });

  const poolsDeployed = !!read.pools;
  const configuredPools = CONTRACTS.XterraPools;
  const configuredIsSet = configuredPools && configuredPools !== ZERO;

  const load = async () => {
    try {
      const [linked, s] = await Promise.all([
        read.xterra.poolsContract().catch(() => ZERO),
        read.pools ? read.pools.getAllPoolStatus().catch(() => null) : null,
      ]);
      setLinkedPools(linked);
      setStatus(s);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const linkedMatches = linkedPools &&
    configuredIsSet &&
    linkedPools.toLowerCase() === configuredPools.toLowerCase();

  async function setPoolsAddress() {
    if (!write) return notify.error('Wallet not connected.');
    if (!configuredIsSet) return notify.error('Set XterraPools address in contracts.js first.');
    setBusy('link');
    try {
      const tx = await write.xterra.setPoolsContract(configuredPools);
      notify.info('Linking Pools to Xterra...');
      await tx.wait();
      notify.success('Pools linked to Xterra.');
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  async function doDistribute(idx) {
    if (!write?.pools) return notify.error('Pools contract not available.');
    const perUser = parseUSDT(distribute[idx].perUser || '0');
    if (perUser.lte(0)) return notify.warning('Enter amount per user.');
    setBusy('dist-' + idx);
    try {
      const tx = await write.pools.distributePool(idx, perUser);
      notify.info(`Creating ${POOL_NAMES[idx]} round...`);
      await tx.wait();
      notify.success(`${POOL_NAMES[idx]} round created.`);
      setDistribute((d) => {
        const c = d.slice();
        c[idx] = { perUser: '' };
        return c;
      });
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  async function doAdminAdd() {
    if (!write?.pools) return notify.error('Pools contract not available.');
    if (!poolAdmin.userId) return notify.warning('Enter a user ID.');
    setBusy('admadd');
    try {
      const tx = await write.pools.adminAddToPool(poolAdmin.userId, Number(poolAdmin.poolId));
      notify.info('Adding to pool...');
      await tx.wait();
      notify.success(`User ${poolAdmin.userId} added to ${POOL_NAMES[Number(poolAdmin.poolId)]}.`);
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  async function doAdminRemove() {
    if (!write?.pools) return notify.error('Pools contract not available.');
    if (!poolAdmin.userId) return notify.warning('Enter a user ID.');
    setBusy('admrm');
    try {
      const tx = await write.pools.adminRemoveFromPool(poolAdmin.userId);
      notify.info('Removing from pool...');
      await tx.wait();
      notify.success(`User ${poolAdmin.userId} removed.`);
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="admin-panel">
      <h3>Pool Rewards (X1 / X2 / X3)</h3>
      <p className="panel-hint">
        Manage the pools contract, view accumulated turnover, create distribution rounds, tune thresholds, and browse members.
      </p>

      {/* Link status */}
      <div style={{
        padding: 16,
        background: linkedMatches ? 'rgba(0,229,160,0.06)' : 'rgba(255,176,32,0.08)',
        border: `1px solid ${linkedMatches ? 'rgba(0,229,160,0.3)' : 'rgba(255,176,32,0.35)'}`,
        borderRadius: 12,
        marginBottom: 20,
      }}>
        <div style={{
          fontWeight: 700,
          color: linkedMatches ? 'var(--accent)' : 'var(--warning)',
          marginBottom: 6,
        }}>
          {linkedMatches ? '✓ Pools linked to Xterra' : '⚠ Pools NOT linked to Xterra'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          On-chain: {linkedPools ? shortenAddress(linkedPools) : '—'} · Configured: {configuredIsSet ? shortenAddress(configuredPools) : 'not set'}
        </div>
        {configuredIsSet && !linkedMatches && (
          <button
            className="btn btn-primary btn-sm mt-4"
            onClick={setPoolsAddress}
            disabled={busy === 'link'}
          >
            {busy === 'link' ? <Spinner /> : '🔗 Link Pools to Xterra'}
          </button>
        )}
        {!configuredIsSet && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            Fill <code>XterraPools</code> address in <code>src/config/contracts.js</code> after deployment.
          </div>
        )}
      </div>

      {!poolsDeployed ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
          XterraPools contract not deployed yet.
        </div>
      ) : status ? (
        <>
          {/* Eligible members — top of the tab, per-pool tabbed browser */}
          <h4 style={{ marginTop: 24, marginBottom: 12, color: 'var(--accent)' }}>Eligible Members Per Pool</h4>
          <PoolEligibleMembers counts={status.memberCountList} />

          {/* Pool status + distribute + members */}
          <h4 style={{ marginTop: 24, marginBottom: 12, color: 'var(--accent)' }}>Pool Status & Distribution</h4>
          <div style={{ display: 'grid', gap: 12 }}>
            {POOL_NAMES.map((name, i) => (
              <div
                key={i}
                style={{
                  padding: 18,
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <strong>{name}</strong>
                  <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                    {(Number(status.bpsList[i]) / 100).toFixed(2)}% of turnover
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, fontSize: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ color: 'var(--text-muted)' }}>Accumulated</div>
                    <strong style={{ fontFamily: 'var(--font-mono)' }}>{formatUSDT(status.accumulatedList[i])} USDT</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)' }}>Members</div>
                    <strong style={{ fontFamily: 'var(--font-mono)' }}>{Number(status.memberCountList[i])}</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)' }}>Current Round</div>
                    <strong style={{ fontFamily: 'var(--font-mono)' }}>{Number(status.currentRoundList[i])}</strong>
                  </div>
                </div>

                {/* Distribute row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'end' }}>
                  <div>
                    <label className="small-label">Amount per user (USDT)</label>
                    <input
                      className="input"
                      value={distribute[i].perUser}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setDistribute((d) => {
                          const c = d.slice();
                          c[i] = { perUser: v };
                          return c;
                        });
                      }}
                      placeholder="e.g. 10"
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => doDistribute(i)}
                    disabled={busy === 'dist-' + i || Number(status.memberCountList[i]) === 0}
                  >
                    {busy === 'dist-' + i ? <Spinner /> : 'Create Round'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setOpenMembers(openMembers === i ? -1 : i)}
                    disabled={Number(status.memberCountList[i]) === 0}
                    title="View members in this pool"
                  >
                    {openMembers === i ? '× Close' : '👥 Members'}
                  </button>
                </div>

                {/* Members drawer */}
                <PoolMembersDrawer
                  poolId={i}
                  open={openMembers === i}
                  onClose={() => setOpenMembers(-1)}
                />
              </div>
            ))}
          </div>

          {/* Thresholds editor */}
          <h4 style={{ marginTop: 28, marginBottom: 12, color: 'var(--accent)' }}>Pool Thresholds</h4>
          <ThresholdsEditor onSaved={load} />

          {/* Admin override */}
          <h4 style={{ marginTop: 28, marginBottom: 12, color: 'var(--accent)' }}>Admin Override</h4>
          <div className="admin-form-row">
            <div className="fields">
              <div>
                <label className="small-label">User ID</label>
                <input
                  className="input"
                  value={poolAdmin.userId}
                  onChange={(e) => setPoolAdmin({ ...poolAdmin, userId: e.target.value.replace(/\D/g, '') })}
                  placeholder="e.g. 5001"
                />
              </div>
              <div>
                <label className="small-label">Pool</label>
                <select
                  className="select"
                  value={poolAdmin.poolId}
                  onChange={(e) => setPoolAdmin({ ...poolAdmin, poolId: e.target.value })}
                >
                  <option value="0">X1</option>
                  <option value="1">X2</option>
                  <option value="2">X3</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={doAdminAdd} disabled={busy === 'admadd'}>
                {busy === 'admadd' ? <Spinner /> : 'Add to Pool'}
              </button>
              <button className="btn btn-danger" onClick={doAdminRemove} disabled={busy === 'admrm'}>
                {busy === 'admrm' ? <Spinner /> : 'Remove'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>
      )}
    </div>
  );
}
